import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  getGoogleWebClientId,
  getAppleWebClientId,
  getAppleRedirectUri,
} from '@/config/runtimeConfig';
import {
  redirectAfterAuth,
  getJwtPayloadSub,
  dismissGoogleSignInUi,
  isStaffPanelUser,
  STAFF_PUBLIC_LOGIN_MESSAGE,
} from '@/utils/oauth';
import { signInWithAppleWeb } from '@/utils/appleSignIn';
import { useGoogleSignInTrigger } from '@/hooks/useGoogleSignInTrigger';

const SOCIAL_BTN_HEIGHT = 'h-12';

const GoogleGIcon = () => (
  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const SocialLoginButtons = ({ disabled = false }) => {
  const navigate = useNavigate();
  const { googleSignIn, appleSignIn, logout } = useAuth();
  const [busy, setBusy] = useState(null);
  const [localError, setLocalError] = useState('');

  const googleClientId = getGoogleWebClientId();
  const appleClientId = getAppleWebClientId();
  const hasGoogle = Boolean(googleClientId);
  const hasApple = Boolean(appleClientId);

  const parseApiError = (err) => {
    const d = err.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && d.message) return d.message;
    return err.message || 'Não foi possível entrar. Tente novamente.';
  };

  const finishOAuth = async (promise) => {
    setLocalError('');
    try {
      const { user } = await promise;
      if (isStaffPanelUser(user)) {
        logout();
        setLocalError(STAFF_PUBLIC_LOGIN_MESSAGE);
        dismissGoogleSignInUi();
        return;
      }
      dismissGoogleSignInUi();
      redirectAfterAuth(user, navigate);
    } catch (err) {
      setLocalError(parseApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const handleGoogleSuccess = useCallback(
    (credentialResponse) => {
      const token = credentialResponse?.credential;
      if (!token) {
        setLocalError('Não foi possível obter o token do Google.');
        return;
      }
      setBusy('google');
      finishOAuth(googleSignIn(token));
    },
    [googleSignIn, navigate]
  );

  const handleGoogleError = useCallback(() => {
    setLocalError(
      'Login com Google indisponível. Confira no Google Cloud as origens autorizadas deste domínio.'
    );
  }, []);

  const { hiddenHostRef, triggerSignIn, ready: googleReady } = useGoogleSignInTrigger({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const handleGooglePress = () => {
    if (!googleReady) {
      setLocalError('Aguarde um instante e tente novamente.');
      return;
    }
    setLocalError('');
    const triggered = triggerSignIn();
    if (!triggered) {
      setLocalError('Não foi possível abrir o login do Google. Recarregue a página.');
    }
  };

  const handleApple = async () => {
    setBusy('apple');
    setLocalError('');
    try {
      const response = await signInWithAppleWeb({
        clientId: appleClientId,
        redirectURI: getAppleRedirectUri(),
      });
      const idToken = response?.authorization?.id_token;
      if (!idToken) {
        setLocalError('Não foi possível obter o token da Apple.');
        setBusy(null);
        return;
      }
      const sub = getJwtPayloadSub(idToken);
      const appleUser = response?.user;
      const fullName = appleUser?.name
        ? [appleUser.name.firstName, appleUser.name.lastName].filter(Boolean).join(' ').trim()
        : null;
      await finishOAuth(
        appleSignIn({
          identity_token: idToken,
          user_identifier: sub || idToken.slice(0, 32),
          email: appleUser?.email || null,
          full_name: fullName || null,
          role: 'client',
        })
      );
    } catch (err) {
      if (err?.error === 'popup_closed_by_user') {
        setBusy(null);
        return;
      }
      setLocalError(err.message || 'Não foi possível entrar com a Apple.');
      setBusy(null);
    }
  };

  const isDisabled = disabled || Boolean(busy);
  const googleDisabled = isDisabled || !googleReady;

  if (!hasGoogle && !hasApple) {
    return null;
  }

  return (
    <div className="space-y-3 w-full min-w-0">
      {localError ? (
        <p className="text-sm text-red-600 dark:text-red-400 text-center" role="alert">
          {localError}
        </p>
      ) : null}

      {hasGoogle ? (
        <>
          <button
            type="button"
            onClick={handleGooglePress}
            disabled={googleDisabled}
            className={`w-full min-w-0 ${SOCIAL_BTN_HEIGHT} flex items-center justify-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-white text-gray-800 px-4 text-sm font-semibold shadow-sm transition-colors hover:bg-gray-50 active:bg-gray-100 disabled:opacity-60 touch-manipulation`}
            aria-label="Continuar com Google"
          >
            <GoogleGIcon />
            <span className="truncate">
              {busy === 'google' ? 'Entrando…' : 'Continuar com Google'}
            </span>
          </button>
          <div
            ref={hiddenHostRef}
            className="google-oauth-hidden-host"
            aria-hidden
            tabIndex={-1}
          />
        </>
      ) : null}

      {hasApple ? (
        <button
          type="button"
          onClick={handleApple}
          disabled={isDisabled}
          className={`w-full min-w-0 ${SOCIAL_BTN_HEIGHT} flex items-center justify-center gap-2 rounded-full border border-gray-300 dark:border-gray-600 bg-black text-white px-4 text-sm font-semibold hover:bg-gray-900 transition-colors disabled:opacity-60 touch-manipulation`}
        >
          <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.36-1.32 2.7-2.54 3.08l-.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
          </svg>
          <span className="truncate">{busy === 'apple' ? 'Entrando…' : 'Continuar com Apple'}</span>
        </button>
      ) : null}
    </div>
  );
};

export default SocialLoginButtons;
